/**
 * Tremoria Offline Store
 * IndexedDB wrapper for managing offline data and sync queue
 */

const DB_NAME = 'tremoria-offline';
const DB_VERSION = 2;

// Store names
const STORES = {
  SYNC_QUEUE: 'sync-queue',
  OFFLINE_DATA: 'offline-data',
  PENDING_UPLOADS: 'pending-uploads',
} as const;

// Sync operation types
export type SyncOperationType = 
  | 'CREATE_MESSAGE'
  | 'UPDATE_MESSAGE'
  | 'DELETE_MESSAGE'
  | 'CREATE_MEMORY'
  | 'UPDATE_MEMORY'
  | 'DELETE_MEMORY'
  | 'CREATE_MONTAGE'
  | 'UPDATE_MONTAGE'
  | 'DELETE_MONTAGE'
  | 'UPDATE_DISPLAY_CONFIG'
  | 'UPLOAD_MEDIA'
  | 'DELETE_MEDIA'
  | 'REORDER_MEDIA';

// Sync queue item
export interface SyncQueueItem {
  id: string;
  operationType: SyncOperationType;
  jewelryId: string;
  deviceCode?: string;
  payload: unknown;
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'processing' | 'failed';
  errorMessage?: string;
}

// Pending upload item (for media files)
export interface PendingUpload {
  id: string;
  jewelryId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileBlob: Blob;
  tempId: string;
  timestamp: number;
  status: 'pending' | 'processing' | 'failed';
  errorMessage?: string;
}

// Offline data cache
export interface OfflineDataItem {
  key: string;
  data: unknown;
  timestamp: number;
  expiresAt?: number;
}

/**
 * Open IndexedDB connection
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Sync queue store
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
        syncStore.createIndex('status', 'status', { unique: false });
        syncStore.createIndex('jewelryId', 'jewelryId', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Offline data store
      if (!db.objectStoreNames.contains(STORES.OFFLINE_DATA)) {
        db.createObjectStore(STORES.OFFLINE_DATA, { keyPath: 'key' });
      }

      // Pending uploads store
      if (!db.objectStoreNames.contains(STORES.PENDING_UPLOADS)) {
        const uploadStore = db.createObjectStore(STORES.PENDING_UPLOADS, { keyPath: 'id' });
        uploadStore.createIndex('jewelryId', 'jewelryId', { unique: false });
        uploadStore.createIndex('status', 'status', { unique: false });
      }
    };
  });
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// ============ Sync Queue Operations ============

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(
  operationType: SyncOperationType,
  jewelryId: string,
  payload: unknown,
  deviceCode?: string
): Promise<string> {
  const db = await openDB();
  const id = generateId();

  const item: SyncQueueItem = {
    id,
    operationType,
    jewelryId,
    deviceCode,
    payload,
    timestamp: Date.now(),
    retryCount: 0,
    status: 'pending',
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.add(item);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // Trigger background sync if available
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then((registration) => {
          (registration as unknown as { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-content');
        }).catch(console.error);
      }
      resolve(id);
    };
  });
}

/**
 * Get all pending sync items
 */
export async function getPendingSyncItems(): Promise<SyncQueueItem[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Get sync queue count
 */
export async function getSyncQueueCount(): Promise<number> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('status');
    const request = index.count('pending');

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Update sync item status
 */
export async function updateSyncItemStatus(
  id: string,
  status: SyncQueueItem['status'],
  errorMessage?: string
): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const getRequest = store.get(id);

    getRequest.onerror = () => reject(getRequest.error);
    getRequest.onsuccess = () => {
      const item = getRequest.result as SyncQueueItem | undefined;
      if (!item) {
        resolve();
        return;
      }

      item.status = status;
      if (errorMessage) {
        item.errorMessage = errorMessage;
        item.retryCount += 1;
      }

      const putRequest = store.put(item);
      putRequest.onerror = () => reject(putRequest.error);
      putRequest.onsuccess = () => resolve();
    };
  });
}

/**
 * Remove item from sync queue
 */
export async function removeFromSyncQueue(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Clear all sync queue items for a jewelry
 */
export async function clearSyncQueueForJewelry(jewelryId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('jewelryId');
    const request = index.openCursor(IDBKeyRange.only(jewelryId));

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
  });
}

// ============ Pending Uploads Operations ============

/**
 * Add pending upload
 */
export async function addPendingUpload(
  jewelryId: string,
  file: File,
  tempId: string
): Promise<string> {
  const db = await openDB();
  const id = generateId();

  const item: PendingUpload = {
    id,
    jewelryId,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    fileBlob: file,
    tempId,
    timestamp: Date.now(),
    status: 'pending',
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_UPLOADS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_UPLOADS);
    const request = store.add(item);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // Trigger background sync
      if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready.then((registration) => {
          (registration as unknown as { sync: { register: (tag: string) => Promise<void> } }).sync.register('upload-media');
        }).catch(console.error);
      }
      resolve(id);
    };
  });
}

/**
 * Get pending uploads
 */
export async function getPendingUploads(): Promise<PendingUpload[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_UPLOADS, 'readonly');
    const store = tx.objectStore(STORES.PENDING_UPLOADS);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Get pending uploads count
 */
export async function getPendingUploadsCount(): Promise<number> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_UPLOADS, 'readonly');
    const store = tx.objectStore(STORES.PENDING_UPLOADS);
    const index = store.index('status');
    const request = index.count('pending');

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Remove pending upload
 */
export async function removePendingUpload(id: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_UPLOADS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_UPLOADS);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// ============ Offline Data Cache Operations ============

/**
 * Store offline data
 */
export async function storeOfflineData(
  key: string,
  data: unknown,
  ttlMs?: number
): Promise<void> {
  const db = await openDB();

  const item: OfflineDataItem = {
    key,
    data,
    timestamp: Date.now(),
    expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.OFFLINE_DATA, 'readwrite');
    const store = tx.objectStore(STORES.OFFLINE_DATA);
    const request = store.put(item);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get offline data
 */
export async function getOfflineData<T>(key: string): Promise<T | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.OFFLINE_DATA, 'readonly');
    const store = tx.objectStore(STORES.OFFLINE_DATA);
    const request = store.get(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const item = request.result as OfflineDataItem | undefined;
      if (!item) {
        resolve(null);
        return;
      }

      // Check expiration
      if (item.expiresAt && Date.now() > item.expiresAt) {
        // Delete expired item
        const deleteTx = db.transaction(STORES.OFFLINE_DATA, 'readwrite');
        deleteTx.objectStore(STORES.OFFLINE_DATA).delete(key);
        resolve(null);
        return;
      }

      resolve(item.data as T);
    };
  });
}

/**
 * Delete offline data
 */
export async function deleteOfflineData(key: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.OFFLINE_DATA, 'readwrite');
    const store = tx.objectStore(STORES.OFFLINE_DATA);
    const request = store.delete(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// ============ Utility Functions ============

/**
 * Get total pending items count (sync queue + uploads)
 */
export async function getTotalPendingCount(): Promise<number> {
  const [syncCount, uploadCount] = await Promise.all([
    getSyncQueueCount(),
    getPendingUploadsCount(),
  ]);
  return syncCount + uploadCount;
}

/**
 * Clear all offline data
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await openDB();

  await Promise.all([
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const request = tx.objectStore(STORES.SYNC_QUEUE).clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    }),
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.OFFLINE_DATA, 'readwrite');
      const request = tx.objectStore(STORES.OFFLINE_DATA).clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    }),
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.PENDING_UPLOADS, 'readwrite');
      const request = tx.objectStore(STORES.PENDING_UPLOADS).clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    }),
  ]);
}

/**
 * Export the store names for service worker
 */
export { STORES, DB_NAME, DB_VERSION };
